/**
 * Times in the store's own time zone (its settings), never the browser's: an owner abroad sets a
 * popup to start at 09:00 store time. `<input type="datetime-local">` holds a wall-clock time
 * without a zone ("2026-10-04T14:30"); the API wants an instant with its offset.
 */

const LOCAL_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;

function two(value: number): string {
  return String(value).padStart(2, '0');
}

interface WallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const partFormatters = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(timeZone: string): Intl.DateTimeFormat {
  let found = partFormatters.get(timeZone);
  if (found === undefined) {
    found = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    partFormatters.set(timeZone, found);
  }
  return found;
}

/** The wall clock in `timeZone` at an instant. */
function wallClockAt(epochMs: number, timeZone: string): WallClock {
  const values: Record<string, number> = {};
  for (const part of partsFormatter(timeZone).formatToParts(new Date(epochMs))) {
    if (part.type !== 'literal') values[part.type] = Number(part.value);
  }
  return {
    year: values['year'] ?? 0,
    month: values['month'] ?? 1,
    day: values['day'] ?? 1,
    hour: values['hour'] ?? 0,
    minute: values['minute'] ?? 0,
    second: values['second'] ?? 0,
  };
}

function asUtc(clock: WallClock): number {
  return Date.UTC(clock.year, clock.month - 1, clock.day, clock.hour, clock.minute, clock.second);
}

/** How far `timeZone` is ahead of UTC at an instant, in milliseconds. */
export function zoneOffsetMs(epochMs: number, timeZone: string): number {
  const whole = epochMs - (epochMs % 1000);
  return asUtc(wallClockAt(whole, timeZone)) - whole;
}

function formatOffset(offsetMs: number): string {
  const minutes = Math.round(offsetMs / 60_000);
  const sign = minutes >= 0 ? '+' : '-';
  const absolute = Math.abs(minutes);
  return `${sign}${two(Math.floor(absolute / 60))}:${two(absolute % 60)}`;
}

/**
 * The instant a wall-clock time in `timeZone` names. Around a clock change: a time that does not
 * exist (the hour skipped in spring) moves forward by the gap, as clocks do; a time that exists
 * twice (the hour repeated in autumn) is its first occurrence.
 */
export function zonedTimeToEpoch(clock: WallClock, timeZone: string): number {
  const guess = asUtc(clock);
  const day = 86_400_000;
  // The offsets in force around that day (two at most: zones change their clocks rarely).
  const offsets = [
    ...new Set([
      zoneOffsetMs(guess - day, timeZone),
      zoneOffsetMs(guess, timeZone),
      zoneOffsetMs(guess + day, timeZone),
    ]),
  ].sort((first, second) => second - first);
  // The largest offset gives the earliest instant: the first occurrence of a repeated time.
  for (const offset of offsets) {
    const epoch = guess - offset;
    if (zoneOffsetMs(epoch, timeZone) === offset) return epoch;
  }
  // No offset fits: the time was skipped. Read it with the offset before the change, which lands
  // after the gap (02:30 on the spring change day becomes 03:30).
  return guess - Math.min(...offsets);
}

/**
 * "2026-10-04T14:30" in `timeZone` → "2026-10-04T14:30:00+03:00"; empty → null; malformed →
 * "invalid" (the contract rejects it at the field).
 */
export function zonedInputToIso(value: string, timeZone: string): string | null {
  const text = value.trim();
  if (text === '') return null;
  const match = LOCAL_PATTERN.exec(text);
  if (match === null) return 'invalid';
  const [, year, month, day, hour, minute, second = '00'] = match;
  const clock = {
    year: Number(year),
    month: Number(month),
    day: Number(day),
    hour: Number(hour),
    minute: Number(minute),
    second: Number(second),
  };
  // Reject dates that do not exist (2026-02-30) instead of rolling them over.
  const check = new Date(asUtc(clock));
  if (
    clock.month < 1 ||
    clock.month > 12 ||
    check.getUTCDate() !== clock.day ||
    clock.hour > 23 ||
    clock.minute > 59 ||
    clock.second > 59
  ) {
    return 'invalid';
  }
  const epoch = zonedTimeToEpoch(clock, timeZone);
  const shown = wallClockAt(epoch, timeZone);
  return (
    `${String(shown.year)}-${two(shown.month)}-${two(shown.day)}` +
    `T${two(shown.hour)}:${two(shown.minute)}:${two(shown.second)}` +
    formatOffset(zoneOffsetMs(epoch, timeZone))
  );
}

/** An instant from the API → the value of a datetime-local input, in `timeZone`. */
export function isoToZonedInput(iso: string | null, timeZone: string): string {
  if (iso === null) return '';
  const epoch = Date.parse(iso);
  if (Number.isNaN(epoch)) return '';
  const clock = wallClockAt(epoch, timeZone);
  return (
    `${String(clock.year)}-${two(clock.month)}-${two(clock.day)}` +
    `T${two(clock.hour)}:${two(clock.minute)}`
  );
}
